/**
 * MyColean Sachet Upsell
 *
 * Manages sachet cross-sell tied to bundle purchases.
 * Sachets are linked to bundles via cart line item properties (_bundle_parent).
 * Removing a bundle auto-removes its linked sachets (checked on page load).
 */
(function () {
  'use strict';

  // Prevent double-init (script may load from both product template and cart drawer)
  if (window.__mcSachetUpsellInit) return;
  window.__mcSachetUpsellInit = true;

  var MIN_SACHETS = 3;
  var MAX_SACHETS = 4;
  var BUNDLE_PROPERTY_KEY = '_bundle_parent';

  // Bundle collection handle → sachet flavor group
  // Product page uses collection handles (from mc-bundle-selector)
  // Cart uses product handles (from /cart.js item.handle)
  var BUNDLE_SACHET_MAP = {
    // Collection handles (product page bundle selector)
    'classic-bundle-collection': 'tropical',
    'tropical-bundle': 'classic',
    'max-strength-bundle': 'all',
    // Product handles (cart items)
    '4-pack-bundle-test': 'tropical',
    'tropical-twist-bundle-4-pack': 'classic',
    'monthly-subscription-super-max-strength-bundle': 'all'
  };

  // XHR helper — bypasses third-party fetch interceptors (upcart) with 429 retry
  function cartFetch(url, options, maxRetries) {
    maxRetries = maxRetries || 3;
    var attempt = 0;
    function doRequest() {
      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open(options.method || 'GET', url, true);
        if (options.headers) {
          Object.keys(options.headers).forEach(function (key) {
            xhr.setRequestHeader(key, options.headers[key]);
          });
        }
        xhr.onload = function () {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            json: function () {
              return Promise.resolve(JSON.parse(xhr.responseText));
            },
          });
        };
        xhr.onerror = function () {
          reject(new Error('Network error'));
        };
        xhr.send(options.body || null);
      });
    }
    function tryRequest() {
      return doRequest().then(function (response) {
        if (response.status === 429 && attempt < maxRetries) {
          attempt++;
          var delay = Math.pow(2, attempt) * 1000;
          return new Promise(function (r) {
            setTimeout(r, delay);
          }).then(tryRequest);
        }
        return response;
      });
    }
    return tryRequest();
  }

  function getCart() {
    return cartFetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      return r.json();
    });
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ── Product Page Logic ──

  // Track whether user selected subscription on the product page
  var isSubscriptionSelected = false;

  function getSelectedSellingPlan() {
    var input = document.querySelector('[data-mc-selling-plan]');
    return input ? input.value : '';
  }

  function initProductPageUpsell() {
    var container = document.querySelector(
      '[data-mc-sachet-upsell][data-context="product"]'
    );
    if (!container) return;

    var cards = container.querySelectorAll('[data-sachet-card]');
    var addBtn = container.querySelector('[data-sachet-add-btn]');
    var totalEl = container.querySelector('[data-sachet-total]');
    var minMsg = container.querySelector('[data-sachet-min-msg]');
    var selectedVariants = new Set();

    // Listen for subscription option changes (delegate on document)
    document.addEventListener('click', function (e) {
      var subOption = e.target.closest('[data-mc-sub-option]');
      if (!subOption) return;
      var planId = subOption.getAttribute('data-selling-plan') || '';
      isSubscriptionSelected = planId !== '';
      updateSachetPriceDisplay(cards);
      updateFooter(container, selectedVariants, cards, addBtn, totalEl, minMsg);
    });

    // Listen for bundle selection changes from mc-bundle-selector
    document.addEventListener('mc-bundle-selection-change', function () {
      selectedVariants.clear();
      cards.forEach(function (c) { c.classList.remove('is-selected'); });
      window.mcSachetTotal = 0;
      updateVisibleSachets(container, cards);
      updateFooter(container, selectedVariants, cards, addBtn, totalEl, minMsg);
    });

    // Initial check
    updateVisibleSachets(container, cards);

    // Card click toggle
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        var variantId = card.dataset.variantId;

        if (card.classList.contains('is-selected')) {
          card.classList.remove('is-selected');
          selectedVariants.delete(variantId);
        } else {
          if (selectedVariants.size >= MAX_SACHETS) return;
          card.classList.add('is-selected');
          selectedVariants.add(variantId);
        }

        updateFooter(container, selectedVariants, cards, addBtn, totalEl, minMsg);
      });
    });

    // Expose sachet selections globally so the main "Add to Cart" can include them
    // No separate add button on product page — sachets go with the bundle
    window.mcSachetSelections = {
      getItems: function () {
        var bundleHandle = getSelectedBundleHandle();
        var sellingPlan = getSelectedSellingPlan();
        var items = [];
        selectedVariants.forEach(function (variantId) {
          var card = container.querySelector('[data-sachet-card][data-variant-id="' + variantId + '"]');
          var sachetPlanId = (card && sellingPlan) ? card.dataset.sellingPlanId : '';
          var item = {
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: {
              _bundle_parent: bundleHandle,
              _sachet_upsell: 'true',
            },
          };
          if (sachetPlanId) {
            item.selling_plan = parseInt(sachetPlanId, 10);
          }
          items.push(item);
        });
        return items;
      },
      getCount: function () {
        return selectedVariants.size;
      },
      clear: function () {
        selectedVariants.clear();
        cards.forEach(function (c) { c.classList.remove('is-selected'); });
        window.mcSachetTotal = 0;
        updateFooter(container, selectedVariants, cards, addBtn, totalEl, minMsg);
      }
    };
  }

  // Toggle sachet card prices between regular and subscription
  function updateSachetPriceDisplay(cards) {
    cards.forEach(function (card) {
      var regularPriceEl = card.querySelector('[data-sachet-card-price]');
      var subPriceEl = card.querySelector('[data-sachet-card-sub-price]');
      if (!subPriceEl) return; // no subscription available for this sachet

      if (isSubscriptionSelected) {
        regularPriceEl.classList.add('mc-sachet-upsell__card-price--crossed');
        subPriceEl.style.display = '';
      } else {
        regularPriceEl.classList.remove('mc-sachet-upsell__card-price--crossed');
        subPriceEl.style.display = 'none';
      }
    });
  }

  function getSelectedBundleHandle() {
    if (!window.mcBundleSelections) return '';
    var handle = '';
    Object.keys(window.mcBundleSelections).forEach(function (key) {
      var sel = window.mcBundleSelections[key];
      if (sel.items && sel.items.size > 0) {
        sel.items.forEach(function (item) {
          if (item.handle) handle = item.handle;
        });
      }
    });
    return handle;
  }

  function updateVisibleSachets(container, cards) {
    var bundleHandle = getSelectedBundleHandle();
    var group = BUNDLE_SACHET_MAP[bundleHandle] || null;

    if (!group) {
      container.dataset.hidden = 'true';
      return;
    }

    container.dataset.hidden = 'false';

    cards.forEach(function (card) {
      var cardGroup = card.dataset.flavorGroup;
      if (group === 'all' || cardGroup === group) {
        card.style.display = '';
      } else {
        card.style.display = 'none';
        card.classList.remove('is-selected');
      }
    });
  }

  function updateFooter(container, selectedVariants, cards, addBtn, totalEl, minMsg) {
    var totalCents = 0;
    cards.forEach(function (card) {
      if (card.classList.contains('is-selected')) {
        var price = parseInt(card.dataset.price, 10);
        if (isSubscriptionSelected && card.dataset.subDiscount) {
          var discount = parseInt(card.dataset.subDiscount, 10);
          price = price - Math.floor(price * discount / 100);
        }
        totalCents += price;
      }
    });

    totalEl.textContent = formatMoney(totalCents);

    // Expose sachet total for main price display
    window.mcSachetTotal = totalCents;
    document.dispatchEvent(new CustomEvent('mc-sachet-selection-change', {
      detail: { total: totalCents, count: selectedVariants.size }
    }));

    var count = selectedVariants.size;
    if (count < MIN_SACHETS) {
      minMsg.textContent = 'Select at least ' + MIN_SACHETS;
      minMsg.style.display = '';
      if (addBtn) addBtn.disabled = true;
    } else if (count >= MAX_SACHETS) {
      minMsg.textContent = 'Maximum ' + MAX_SACHETS + ' selected';
      minMsg.style.display = '';
      if (addBtn) addBtn.disabled = false;
    } else {
      minMsg.style.display = 'none';
      if (addBtn) addBtn.disabled = false;
    }
  }

  // ── Cart Logic: Show sachets for bundles in cart ──

  function initCartUpsell() {
    var containers = document.querySelectorAll(
      '[data-mc-sachet-upsell][data-context="cart"]'
    );
    if (containers.length === 0) return;

    // Check cart on page load (with a small delay to avoid competing with other scripts)
    setTimeout(function () {
      checkCartForBundles(containers);
    }, 1500);
  }

  // Known product handles that belong to bundle collections
  var BUNDLE_PRODUCT_HANDLES = null; // populated on first cart check

  function findBundleHandlesInCart(items) {
    var bundleHandles = [];

    // Check for _bundle_handle property on items (set when adding via bundle selector)
    items.forEach(function (item) {
      if (item.properties && item.properties._bundle_handle && BUNDLE_SACHET_MAP[item.properties._bundle_handle]) {
        var handle = item.properties._bundle_handle;
        if (bundleHandles.indexOf(handle) === -1) {
          bundleHandles.push(handle);
        }
      }
    });

    // Also check product handles directly (for cart items from bundles)
    items.forEach(function (item) {
      if (item.handle && BUNDLE_SACHET_MAP[item.handle]) {
        if (bundleHandles.indexOf(item.handle) === -1) {
          bundleHandles.push(item.handle);
        }
      }
    });

    return bundleHandles;
  }

  // Check if cart has any non-sachet items (i.e., actual bundle products)
  function cartHasNonSachetItems(items) {
    return items.some(function (item) {
      return !(item.properties && item.properties._sachet_upsell === 'true');
    });
  }

  function checkCartForBundles(containers) {
    getCart().then(function (cart) {
      var items = cart.items || [];
      var bundleHandles = findBundleHandlesInCart(items);

      // Remove orphaned sachets (runs once on page load)
      removeOrphanedSachets(items, bundleHandles);

      containers.forEach(function (container) {
        var cards = container.querySelectorAll('[data-sachet-card]');

        if (bundleHandles.length === 0) {
          container.dataset.hidden = 'true';
          return;
        }

        var groups = {};
        bundleHandles.forEach(function (handle) {
          var g = BUNDLE_SACHET_MAP[handle];
          if (g === 'all') {
            groups['classic'] = true;
            groups['tropical'] = true;
          } else if (g) {
            groups[g] = true;
          }
        });

        var sachetAlreadyInCart = items.some(function (item) {
          return (
            item.properties &&
            item.properties._sachet_upsell === 'true'
          );
        });

        if (sachetAlreadyInCart) {
          container.dataset.hidden = 'true';
          return;
        }

        container.dataset.hidden = 'false';

        cards.forEach(function (card) {
          var cardGroup = card.dataset.flavorGroup;
          if (groups[cardGroup]) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });

        initCartSachetAdd(container, bundleHandles);
      });
    });
  }

  function initCartSachetAdd(container, bundleHandles) {
    var addBtn = container.querySelector('[data-sachet-add-btn]');
    var totalEl = container.querySelector('[data-sachet-total]');
    var minMsg = container.querySelector('[data-sachet-min-msg]');
    var selectedVariants = new Set();

    // Remove old listeners by cloning cards
    var oldCards = container.querySelectorAll('[data-sachet-card]');
    oldCards.forEach(function (card) {
      var newCard = card.cloneNode(true);
      card.parentNode.replaceChild(newCard, card);
    });

    var cards = container.querySelectorAll('[data-sachet-card]');

    var newAddBtn = addBtn.cloneNode(true);
    addBtn.parentNode.replaceChild(newAddBtn, addBtn);
    addBtn = newAddBtn;

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        var variantId = card.dataset.variantId;

        if (card.classList.contains('is-selected')) {
          card.classList.remove('is-selected');
          selectedVariants.delete(variantId);
        } else {
          if (selectedVariants.size >= MAX_SACHETS) return;
          card.classList.add('is-selected');
          selectedVariants.add(variantId);
        }

        updateFooter(container, selectedVariants, cards, addBtn, totalEl, minMsg);
      });
    });

    addBtn.addEventListener('click', function () {
      if (selectedVariants.size < MIN_SACHETS) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Verifying...';

      // Re-validate: ensure bundle is still in cart before adding sachets
      getCart().then(function (cart) {
        var items = cart.items || [];
        var currentBundles = findBundleHandlesInCart(items);

        if (currentBundles.length === 0) {
          addBtn.textContent = 'Bundle removed — cannot add';
          container.dataset.hidden = 'true';
          return;
        }

        var parentHandle = currentBundles[0] || '';

        var sachetItems = [];
        selectedVariants.forEach(function (variantId) {
          sachetItems.push({
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: {
              _bundle_parent: parentHandle,
              _sachet_upsell: 'true',
            },
          });
        });

        addBtn.textContent = 'Adding...';

        return cartFetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: sachetItems }),
        }).then(function (response) {
          if (!response.ok) throw new Error('Failed to add sachets');
          addBtn.textContent = 'Added!';
          container.dataset.hidden = 'true';
          setTimeout(function () {
            window.location.reload();
          }, 600);
        });
      }).catch(function () {
        addBtn.textContent = 'Error - Try Again';
        addBtn.disabled = false;
      });
    });
  }

  // ── Orphan Removal: Remove sachets when their parent bundle is removed ──
  // This runs once on cart page load — no interceptors needed since
  // cart changes trigger page reloads anyway.

  var isRemovingOrphans = false;

  function removeOrphanedSachets(cartItems, activeBundleHandles) {
    if (isRemovingOrphans) return;

    var hasNonSachet = cartHasNonSachetItems(cartItems);
    var orphanKeys = [];

    cartItems.forEach(function (item) {
      if (
        item.properties &&
        item.properties._sachet_upsell === 'true'
      ) {
        // Remove sachet if: no non-sachet items in cart, OR its parent bundle is gone
        if (!hasNonSachet) {
          orphanKeys.push(item.key);
        } else if (item.properties[BUNDLE_PROPERTY_KEY]) {
          var parentHandle = item.properties[BUNDLE_PROPERTY_KEY];
          var parentExists = activeBundleHandles.indexOf(parentHandle) !== -1;
          if (!parentExists) {
            orphanKeys.push(item.key);
          }
        }
      }
    });

    if (orphanKeys.length === 0) return;

    isRemovingOrphans = true;

    var updates = {};
    orphanKeys.forEach(function (key) {
      updates[key] = 0;
    });

    cartFetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates: updates }),
    }).then(function () {
      // Always reload — this is the definitive cleanup
      window.location.reload();
    }).catch(function () {
      // Reset flag on failure so next event can retry
      isRemovingOrphans = false;
    });
  }

  // ── Cart Change Listener: Detect bundle removal via theme event ──

  function initCartListener() {
    // Impulse theme fires 'cart:updated' with full cart data after any cart change
    document.addEventListener('cart:updated', function (e) {
      var cart = e.detail && e.detail.cart;
      if (!cart || !cart.items) return;

      var items = cart.items;

      // Check if any sachets exist in cart
      var hasSachets = items.some(function (item) {
        return item.properties && item.properties._sachet_upsell === 'true';
      });
      if (!hasSachets) return; // nothing to clean up

      var bundleHandles = findBundleHandlesInCart(items);
      removeOrphanedSachets(items, bundleHandles);
    });

    // Also listen for UPCart events (cart drawer app)
    window.addEventListener('aftersell-upcart:public-events:added-to-cart', function () {
      setTimeout(function () {
        getCart().then(function (cart) {
          var items = cart.items || [];
          var bundleHandles = findBundleHandlesInCart(items);
          removeOrphanedSachets(items, bundleHandles);
        });
      }, 1000);
    });
  }

  // ── Checkout Guard: Verify sachets have a bundle before allowing checkout ──

  function initCheckoutGuard() {
    if (window.__mcSachetGuardBoot) return; // never register twice (cart page loads this file from two places)
    window.__mcSachetGuardBoot = true;

    // Checkout must ALWAYS go through the sticky.io redirect, never native
    // Shopify checkout. If the shared redirect exists we delegate to it;
    // otherwise fall back to the original navigation.
    function proceedToCheckout(link) {
      if (window.mcStickyRedirect) {
        window.mcStickyRedirect();
        return;
      }
      if (link && link.href) {
        window.location.href = link.href;
      } else {
        var form = link && link.closest ? link.closest('form') : null;
        if (form) form.submit();
        else window.location.href = '/checkout';
      }
    }

    // Intercept all checkout form submissions and checkout link clicks
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form) return;
      var action = form.getAttribute('action') || '';
      var hasCheckoutBtn = form.querySelector('[name="checkout"]');
      if (action.indexOf('/checkout') !== -1 || hasCheckoutBtn) {
        e.preventDefault();
        verifySachetsBeforeCheckout(function () {
          proceedToCheckout(null);
        });
      }
    }, true);

    // Intercept clicks on checkout buttons/links (UPCart uses its own buttons)
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/checkout"], [data-upcart-checkout]');
      if (!link) {
        // Also catch any button with "checkout" in its class or text
        var btn = e.target.closest('button');
        if (btn && btn.textContent.toLowerCase().indexOf('checkout') !== -1) {
          link = btn;
        }
      }
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();
      verifySachetsBeforeCheckout(function () {
        proceedToCheckout(link);
      });
    }, true);
  }

  function verifySachetsBeforeCheckout(proceed) {
    getCart().then(function (cart) {
      var items = cart.items || [];
      var hasSachets = items.some(function (item) {
        return item.properties && item.properties._sachet_upsell === 'true';
      });

      if (!hasSachets) {
        proceed();
        return;
      }

      // Check if bundles exist
      var bundleHandles = findBundleHandlesInCart(items);
      var hasNonSachet = cartHasNonSachetItems(items);

      if (bundleHandles.length > 0 && hasNonSachet) {
        // All good — sachets have a bundle
        proceed();
        return;
      }

      // Orphaned sachets found — remove them, then proceed
      var orphanKeys = [];
      items.forEach(function (item) {
        if (item.properties && item.properties._sachet_upsell === 'true') {
          orphanKeys.push(item.key);
        }
      });

      var updates = {};
      orphanKeys.forEach(function (key) { updates[key] = 0; });

      cartFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates }),
      }).then(function () {
        proceed();
      }).catch(function () {
        // If cleanup fails, still allow checkout — better than blocking the user
        proceed();
      });
    }).catch(function () {
      proceed();
    });
  }

  // Exposed so the sticky.io redirect (snippets/sticky-checkout-redirect.liquid)
  // can run orphan-sachet cleanup before building the checkout payload.
  window.mcVerifySachetsBeforeCheckout = verifySachetsBeforeCheckout;

  // ── Init ──

  function init() {
    initProductPageUpsell();
    initCartUpsell();
    initCartListener();
    initCheckoutGuard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
